import React, { useEffect, useMemo, useRef, useState } from 'react';

import { Form } from 'antd';
import styled from 'styled-components';

import { createUserManagementUser, updateUserManagementUser } from 'apis/UserAPI';
import { useRootContext } from 'components/layout/RootContext';
import Modal from 'components/lib/Modal';
import { useAppSelector } from 'store/hook';
import type { PositionRightDataType } from 'store/slices/globalSlice';
import { CompanyType } from 'types/company';

import { getInitialEditableUserState, getInitialSelectedCommunities, getNormalizedEditableUserState } from './helpers';
import { UserManagementUserType } from './types';
import UserManagementBasicDataSection from './UserManagementBasicDataSection';
import UserManagementCommunitiesSection from './UserManagementCommunitiesSection';
import { useUserManagementContext } from './UserManagementContext';
import { UserManagementCommunityOption } from './UserManagementSelectedCommunities';
import UserManagementUserModalFooter from './UserManagementUserModalFooter';
import { STATUS } from '../../constants';

const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

const Panel = styled.div`
  display: flex;
  overflow-y: hidden;
  height: 70vh;
`;

const HiddenFormItem = styled(Form.Item)`
  display: none;
`;

type Props = {
  open: boolean;
  onClose: () => void;
  mode: 'create' | 'edit';
  user?: UserManagementUserType;
};

const DEFAULT_FORM_VALUES = {
  first_name: '',
  last_name: '',
  email: '',
  position_id: null,
  is_active: true,
  company_id: null,
  holding_id: null,
  communities_ids: [],
};

const MODAL_WIDTH = 1100;

const UserManagementUserModal = ({ open, onClose, mode, user }: Props) => {
  const { showMessage } = useRootContext();
  const { refetch } = useUserManagementContext();

  const { currentUser } = useAppSelector((state) => state.auth);
  const { companies, positions } = useAppSelector((state) => state.global);

  const isEditMode = mode === 'edit';
  const isAdminUser = Boolean(currentUser?.isSuperuser || currentUser?.isStaff);

  const [form] = Form.useForm();
  const firstName = Form.useWatch('first_name', form);
  const lastName = Form.useWatch('last_name', form);
  const email = Form.useWatch('email', form);
  const companyId = Form.useWatch('company_id', form);
  const holdingId = Form.useWatch('holding_id', form);
  const positionId = Form.useWatch('position_id', form);
  const isActive = Form.useWatch('is_active', form);

  const [status, setStatus] = useState<string>(STATUS.IDLE);
  const [selectedCommunities, setSelectedCommunities] = useState<UserManagementCommunityOption[]>([]);
  const disabledCompanySelector = Boolean(holdingId);
  const disabledHoldingSelector = Boolean(companyId);

  const initialScope = useRef<{ companyId: number | null; holdingId: number | null }>({
    companyId: null,
    holdingId: null,
  });
  const initialEditableState = useRef(getInitialEditableUserState());
  const isInitializingForm = useRef(false);
  const isWaitingForInitialScopeSync = useRef(false);

  useEffect(() => {
    if (!open) {
      return;
    }

    isInitializingForm.current = true;

    if (isEditMode && user) {
      const initialSelectedCommunities = getInitialSelectedCommunities(user);

      initialScope.current = {
        companyId: user.company?.id ?? null,
        holdingId: user.holding?.id ?? null,
      };
      initialEditableState.current = getInitialEditableUserState(user);
      isWaitingForInitialScopeSync.current = true;

      setSelectedCommunities(initialSelectedCommunities);
      form.setFieldsValue({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        email: user.email || '',
        position_id: user.position?.id ?? null,
        is_active: user.is_active,
        company_id: user.company?.id ?? null,
        holding_id: user.holding?.id ?? null,
        communities_ids: initialSelectedCommunities.map((community) => community.id),
      });
    } else {
      initialScope.current = {
        companyId: null,
        holdingId: null,
      };
      initialEditableState.current = getInitialEditableUserState();
      isWaitingForInitialScopeSync.current = false;
      form.resetFields();
      form.setFieldsValue(DEFAULT_FORM_VALUES);
      setSelectedCommunities([]);
    }

    isInitializingForm.current = false;
  }, [open, form, isEditMode, user]);

  useEffect(() => {
    if (!open) {
      return;
    }

    if (isInitializingForm.current) {
      return;
    }

    if (isEditMode) {
      if (isWaitingForInitialScopeSync.current) {
        const normalizedCompanyId = companyId ?? null;
        const normalizedHoldingId = holdingId ?? null;
        const initialCompanyId = initialScope.current.companyId;
        const initialHoldingId = initialScope.current.holdingId;

        if (normalizedCompanyId === initialCompanyId && normalizedHoldingId === initialHoldingId) {
          isWaitingForInitialScopeSync.current = false;
        }
        return;
      }

      const scopeChanged = companyId !== initialScope.current.companyId || holdingId !== initialScope.current.holdingId;
      if (!scopeChanged) {
        return;
      }
    }

    setSelectedCommunities([]);
    form.setFieldsValue({ communities_ids: [] });
  }, [companyId, holdingId, form, isEditMode, open]);

  const companiesOptions = (companies || []).map((company: CompanyType) => ({
    value: company.id,
    name: company.name,
    label: company.name,
  }));

  const positionOptions = useMemo(
    () =>
      (positions || []).map((position: PositionRightDataType) => ({
        value: position.id,
        label: position.name,
      })),
    [positions]
  );

  const selectedPosition = useMemo(
    () => (positions || []).find((position: PositionRightDataType) => position.id === positionId) ?? null,
    [positionId, positions]
  );

  const hasEditableChanges = useMemo(() => {
    if (!isEditMode || !open) {
      return true;
    }

    const currentValues = getNormalizedEditableUserState({
      ...form.getFieldsValue([
        'first_name',
        'last_name',
        'email',
        'position_id',
        'is_active',
        'company_id',
        'holding_id',
      ]),
      communities_ids: selectedCommunities.map((community) => community.id),
    });

    return JSON.stringify(currentValues) !== JSON.stringify(initialEditableState.current);
  }, [
    companyId,
    email,
    firstName,
    form,
    holdingId,
    isActive,
    isEditMode,
    lastName,
    open,
    positionId,
    selectedCommunities,
  ]);

  const handleCompanyClear = () => {
    form.setFieldsValue({ company_id: null });
  };

  const handleCompanySelect = (value: number) => {
    form.setFieldsValue({
      company_id: value,
      holding_id: null,
    });
  };

  const handleHoldingClear = () => {
    form.setFieldsValue({ holding_id: null });
  };

  const handleHoldingSelect = (value: number) => {
    form.setFieldsValue({
      holding_id: value,
      company_id: null,
    });
  };

  const handleSelectedCommunitiesChange = (nextCommunities: UserManagementCommunityOption[]) => {
    setSelectedCommunities(nextCommunities);
    form.setFieldsValue({ communities_ids: nextCommunities.map((community) => community.id) });
  };

  const getFirstErrorMessage = (value: unknown) => {
    if (Array.isArray(value) && value.length > 0) {
      return String(value[0]);
    }

    if (typeof value === 'string') {
      return value;
    }

    return null;
  };

  const handleSubmit = async (sendInvite = false) => {
    let values;

    try {
      values = await form.validateFields();
    } catch (error: any) {
      return;
    }

    try {
      setStatus(STATUS.LOADING);

      if (isEditMode && user) {
        const updatePayload = {
          first_name: values.first_name.trim(),
          last_name: values.last_name.trim(),
          position_id: values.position_id ?? null,
          company_id: isAdminUser ? (values.company_id ?? null) : undefined,
          holding_id: isAdminUser ? (values.holding_id ?? null) : undefined,
          communities_ids: values.communities_ids,
          is_active: values.is_active,
        };
        await updateUserManagementUser(user.id, updatePayload);
        showMessage('success', 'User updated!');
      } else {
        const payload = {
          email: values.email.trim(),
          first_name: values.first_name.trim(),
          last_name: values.last_name.trim(),
          position_id: values.position_id ?? null,
          company_id: isAdminUser ? (values.company_id ?? null) : undefined,
          holding_id: isAdminUser ? (values.holding_id ?? null) : undefined,
          communities_ids: values.communities_ids,
          is_active: values.is_active,
        };
        await createUserManagementUser({
          ...payload,
          send_invite: sendInvite,
        });
        showMessage('success', sendInvite ? 'User created and invitation sent!' : 'User created!');
        form.resetFields();
      }

      refetch();
      onClose();
    } catch (error: any) {
      const errorResponse = error?.response?.data ?? {};
      const emailErrorMessage = getFirstErrorMessage(errorResponse?.email);

      if (emailErrorMessage) {
        form.setFields([
          {
            name: 'email',
            errors: [emailErrorMessage],
          },
        ]);
      }

      showMessage('error', emailErrorMessage || (isEditMode ? 'Failed to update user' : 'Failed to create user'));
      console.error(error);
    } finally {
      setStatus(STATUS.IDLE);
    }
  };

  if (isEditMode && !user) {
    return null;
  }

  return (
    <Modal
      className={'flat'}
      title={<span data-test-id="user-management-modal-title">{isEditMode ? 'Edit User' : 'Add User'}</span>}
      footer={
        <UserManagementUserModalFooter
          isEditMode={isEditMode}
          onClose={onClose}
          onSubmit={handleSubmit}
          status={status}
          disableSubmit={isEditMode && !hasEditableChanges}
        />
      }
      open={open}
      centered
      width={MODAL_WIDTH}
      destroyOnClose
      onCancel={onClose}
    >
      <Container data-test-id="user-management-modal">
        <Form form={form} requiredMark={false} initialValues={DEFAULT_FORM_VALUES}>
          <HiddenFormItem name="communities_ids">
            <input type="hidden" />
          </HiddenFormItem>

          <Panel>
            <UserManagementBasicDataSection
              companiesOptions={companiesOptions}
              disabledCompanySelector={disabledCompanySelector}
              disableEmailEdit={isEditMode}
              disabledHoldingSelector={disabledHoldingSelector}
              isAdminUser={isAdminUser}
              positionOptions={positionOptions}
              selectedPosition={selectedPosition}
              onCompanyClear={handleCompanyClear}
              onCompanySelect={handleCompanySelect}
              onHoldingClear={handleHoldingClear}
              onHoldingSelect={handleHoldingSelect}
            />

            <UserManagementCommunitiesSection
              companyId={companyId}
              holdingId={holdingId}
              isAdminUser={isAdminUser}
              selectedCommunities={selectedCommunities}
              fallbackCompanyName={companies.find((company) => company.id === companyId)?.name}
              onSelectedCommunitiesChange={handleSelectedCommunitiesChange}
            />
          </Panel>
        </Form>
      </Container>
    </Modal>
  );
};

export default UserManagementUserModal;
